import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { ReactComponent as AddCandidates } from '$icons/PersonAdd-White.svg';
import {
  AddMultipleCandidatesToPositionErrorOutput,
  useAddMultipleCandidateToPositionMutation,
} from '$store/modules/candidates';
import Button from '$components/Buttons/Button';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { companiesApi } from '$store/modules/companies';
import { successHandler } from '$store/utils';
import classes from '../SinglePosition.module.css';

const AddCandidatesIntoPosition = ({ positionId }: { positionId: string }) => {
  const [showAddCandidatesModal, setShowAddCandidatesModal] = useState(false);
  const [candidateIdsInput, setCandidateIdsInput] = useState<number[]>([]);
  const [candidateErrorIds, setCandidateErrorIds] = useState<number[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [t] = useTranslation('global');
  const addCandidatesClickHandler = () => {
    setShowAddCandidatesModal(true);
  };
  const [addMultipleCandidatesToPosition, { isLoading, data, error, reset }] =
    useAddMultipleCandidateToPositionMutation();
  const dispatch = useAppDispatch();

  const saveCandidatesHandler = async () => {
    if (textareaRef.current) {
      const candidateIds = textareaRef.current.value
        .split(/,|\n/g)
        .reduce((prev, act) => {
          if (!Number.isNaN(act) && !Number.isNaN(parseInt(act, 10))) {
            prev.push(parseInt(act, 10));
          }
          return prev;
        }, [] as number[]);
      setCandidateIdsInput(candidateIds);
      reset();
      setCandidateErrorIds([]);
      await addMultipleCandidatesToPosition({ positionId, candidateIds });
    }
  };
  const onCloseModal = () => setShowAddCandidatesModal(false);

  useEffect(() => {
    // Successfully added all candidates
    if (!isLoading && data) {
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));
      successHandler('Candidates added successfully', true);
      onCloseModal();
    }

    // An error ocurred
    if (!isLoading && error && 'data' in error) {
      const errorData =
        error.data as AddMultipleCandidatesToPositionErrorOutput['data'];
      // Not all candidates id had an error
      if (errorData.length !== candidateIdsInput.length) {
        dispatch(companiesApi.util.invalidateTags(['PositionsList']));
      }
      const erroredCandidateIds = errorData.map(
        (singleError) => singleError.candidateId
      );
      setCandidateErrorIds(erroredCandidateIds);
    }
  }, [isLoading, data, dispatch, error, candidateIdsInput.length]);
  return (
    <>
      <button
        type="button"
        className={classes.refresh}
        title="Add Candidates"
        onClick={addCandidatesClickHandler}
      >
        <AddCandidates className="fill-white w-6" />
      </button>
      <ModalInTailwind
        isOpen={showAddCandidatesModal}
        closeModal={onCloseModal}
      >
        <div className="bg-white p-4 rounded min-w-52 flex flex-col gap-4">
          <h1>{t('DuplicateJob.PasteCandidatesIds')}</h1>
          <textarea
            ref={textareaRef}
            className="w-full border p-2 border-gray-300 rounded min-h-52"
          />
          {candidateErrorIds.length > 0 && (
            <div className="p-2 bg-red-300 border border-red-600 rounded">
              Unable to add the following candidates:
              <div>{candidateErrorIds.join(', ')}</div>
            </div>
          )}
          <div className="flex flex-row">
            <Button
              bgColor="transparent"
              showText
              textStyles={{ color: '#3e3e3e' }}
              text={t('common.cancel')}
              onClick={() => onCloseModal()}
            />
            <Button
              disabled={isLoading}
              bgColor="#079e88"
              showText
              text={isLoading ? t('common.saving') : t('common.save')}
              onClick={saveCandidatesHandler}
            />
          </div>
        </div>
      </ModalInTailwind>
    </>
  );
};

export default AddCandidatesIntoPosition;
